import {
    Fragment, extractSentences, isBlockElement
} from "./Benespeak.js";

/**
 * @import {TtsSentence} from "./Benespeak.js"
 */

const WORD_CHAR_MATCHER = /\w/;
class TtsPlayer {

    static get SEEK_FORWARD() {
        return 0;
    }

    static get SEEK_BACKWARD() {
        return 1;
    }

    constructor(options={}) {
        /** @type {SpeechSynthesisVoice} */
        this.voice = options.voice || null;
        this.rate = options.rate || 1;
        this.voiceImageDescriptions = options.voiceImageDescriptions || true;
        this.voicePageNumbers = options.voicePageNumbers || false;
        this.treeWalker = null;

        /** @type {Element} Top-most element that the player will attempt to play. Defaults to document.body. */
        this.root = options.root || document.body;

        const noOp = () => {};

        /** @type {Function} */
        this.highlightWord = options.highlightWord || noOp;

        /** @type {Function} */
        this.clearWordHighlight = options.clearWordHighlight || noOp;

        /** @type {Function} */
        this.highlightSentence = options.highlightSentence || noOp;

        /** @type {Function} */
        this.clearSentenceHighlight = options.clearSentenceHighlight || noOp;

        /** @type {Function} */
        this.updateViewerPosition = options.updateViewerPosition || noOp;

        /** @type {Function} */
        this.onSpeechEnd = options.onSpeechEnd || noOp;

        /** @type {Function} */
        this.onOutOfElements = options.onOutOfElements || options.onSpeechEnd || noOp;

        /** @type {Element} The element used to generate current sentence data. */
        this.activeElement = null;

        /** @type {Range} The "virtual cursor position" for the TTS player. */
        this.cursorPosition = null;

        /** @type {Array<TtsSentence>} Sentence data. */
        this.sentences = [];

        /** @type {Number} Array index of the most recently uttered sentence. */
        this.currentSentence = -1;

        /** @type {SpeechSynthesisUtterance} Most recent dispatched Utterance*/
        this.utterance = null;

        // shorter ref to speech synthesis global
        this.synth = options.synth || window.speechSynthesis;

        // Internal state tracking
        this.interrupted = false;
        this.stopped = false;
    }

    /**
     * @param {Element} rootElement Root of the DOM tree that TTS Player should read.
     */
    set root(rootElement) {

        this.reset();

        if (rootElement === null || rootElement === undefined) {
            this.treeWalker = null;
        } else if (rootElement?.nodeType !== Node.ELEMENT_NODE) {
            throw new TypeError('TTSPlayer root must be a DOM element');
        }

        this.treeWalker =
            rootElement?.ownerDocument?.createTreeWalker(
                rootElement,
                NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT,
                (n) => this._nodeFilter(n)
            ) ?? null;
    }

    /**
     * @returns {Element | null} Root of the DOM subtree that the TTS Player is reading.
     */
    get root() {
        return this.treeWalker?.root ?? null;
    }

    /**
     * Starts TTS playback from the sentence closest to the click coordinates in a
     * double-clicked target.
     * @param {MouseEvent} event
     */
    playFromMouseEvent(event) {
        // Interrupt any ongoing speech
        this.interrupt();

        // Bail if it's an anchor tag. Double-clicks typically won't trigger on anchor
        // elements, but best to be safe and ensure we don't run TTS.
        if (event.target.tagName === 'A') {
            this.onSpeechEnd();
            return;
        }

        // Grab the selection
        const selection = event.target.ownerDocument.defaultView.getSelection();

        // Find the closest
        const targetElement = this._findClosestReadableAncestor(selection.anchorNode);

        // Mouse events should not trigger playback on the root element.
        if (targetElement === null || targetElement === this.treeWalker?.root) {
            this.reset();
        } else {
            this._generateSentenceData(targetElement);
        }

        // By default, read from the beginning of the sentence set
        let idx = 0;

        // If we have sentence data, and the selection contains a valid range,
        // check if the range is inside any of the sentence ranges.
        if (this.sentences?.length > 0 && selection.rangeCount === 1) {
            const rangeFromSelection = selection.getRangeAt(0);
            idx = this._findIndexForRange(rangeFromSelection);
        }

        // Delegate to play method; if no sentences were found for targetElement,
        // the play method will attempt to advance to the next readable element,
        // and exits cleanly when no more elements can be consumed.
        this.play(targetElement, idx);
    }

    /**
     * Starts TTS playback from the closest readable Element ancestor of the given DOM node.
     * @param {Node} node
     */
    playFromNode(node) {
        // Interrupt any ongoing speech
        this.interrupt();

        // find closest element parent
        this.play(this._findClosestReadableAncestor(node));
    }

    /**
     * Starts TTS playback from the specified element.
     * @param {Element} element Element to read.
     * @param {Number} [index] Index of sentence to start reading from. If none is provided,
     *                         the reader will attempt to locate the "virtual cursor" in the
     *                         sentence data and read from there.
     */
    play(element, index) {
        this._generateSentenceData(element);

        if (this.sentences.length > 0) {
            if (index !== undefined) {
                this._speakUtterance(index);
            } else {
                this._speakUtterance(this._findIndexForRange(this.cursorPosition));
            }
        } else {
            // No sentences found, try seeking again.
            const nextReadableElement = this._seekToNextReadableElement(element);
            if (nextReadableElement) {
                this.play(nextReadableElement);
            } else {
                this.reset();
                this.onOutOfElements();
            }
        }
    }

    /**
     * Reset TTS sentence and position data.
     */
    reset() {
        this.activeElement = null;
        this.sentences = null;
        this.currentSentence = -1
        this.cursorPosition = null;
    }

    /**
     * Generates sentence data from the specified element.
     * @param {Element} element
     */
    _generateSentenceData(element) {
        if (element !== this.activeElement) {
            console.log(`Generating new data for ${element.tagName}#${element.getAttribute('id')}`);
            this.activeElement = element;
            this.sentences = extractSentences(element, {
                voiceImageDescriptions: this.voiceImageDescriptions,
                voicePageNumbers: this.voicePageNumbers
            });
        }
    }

    /**
     * Speaks the sentence at startIndex. Negative start indexes go from the end of the
     * sentence list (e.g. _speakUtterance(-1) speaks the last sentence).
     * @param {Number} startIndex
     */
    _speakUtterance(startIndex) {
        // timers to pause and resume speech every 10 seconds
        // allows for google voices to not stop in middle of speech
        var timeout = setInterval(function(player){
            if (player?.voice?.name.startsWith("Google")) {
                player.synth.pause();
                player.synth.resume();
            }
        }, 10000, this);
        const idx = (startIndex >= 0) ? startIndex : this.sentences.length + startIndex;
        const s = this.sentences[idx];
        const u = new SpeechSynthesisUtterance(s.utterance);
        u.voice = this.voice;
        u.rate = this.rate;
        u.btIdx = idx;
        const handler = this.makeTtsEventHandler(s, idx, this, timeout);
        u.addEventListener('start', handler);
        u.addEventListener('boundary', handler);
        u.addEventListener('error', handler);
        u.addEventListener('end', handler);
        this.synth.speak(u);
    }

    resume() {
        this.synth.resume();
    }

    pause() {
        this.synth.pause();
    }

    ffwd() {
        this.interrupt();
        const newIndex = this.currentSentence + 1;

        if (newIndex < this.sentences.length) {
            console.log(`Skipping to sentence ${newIndex}`);
            this.play(this.activeElement, newIndex);
        } else {
            const nextReadableElement = this._seekToNextReadableElement(this.activeElement);
            if (nextReadableElement !== null) {
                this.play(nextReadableElement);
            } else {
                // No more readable nodes
                this.reset();
                this.onOutOfElements();
            }
        }

    }

    rew() {
        this.interrupt();
        const newIndex = this.currentSentence - 1;

        if (newIndex >= 0) {
            console.log(`Rewinding to sentence ${newIndex}`);
            this.play(this.activeElement, newIndex);
        } else {
            const nextReadableElement = this._seekToNextReadableElement(this.activeElement, TtsPlayer.SEEK_BACKWARD);
            if (nextReadableElement !== null) {
                this.play(nextReadableElement);
            } else {
                // No more readable nodes
                this.onSpeechEnd();
            }
        }
    }

    /**
     * Cancels all utterances in queue and sets the stopped flag. This will reset player state
     * when the speechEnd event fires.
     */
    stop() {
        this.stopped = true;
        this.synth.cancel();
    }

    /**
     * Cancels all utterances in queue and sets the interrupted flag. The interrupt flag
     * is used for pauses and ffwd/rew to indicate to the player that it should hang on to
     * player state when the speechEnd event fires, as the next utterance will likely be from
     * the current activeElement.
     */
    interrupt() {
        this.interrupted = true;
        this.synth.cancel();
    }

    /**
     * Handler for end-of-utterance event. Since Web Speech doesn't make a distinction between
     * the utterance coming to a natural stop or being manually canceled/interrupted, we need
     * to perform some checks to decide the correct course of action.
     */
    handleSentenceEnd() { 
        if (this.stopped) {
            // Manually stopped. Data should be cleared out.
            this.reset();
            this.onSpeechEnd();
        } else if (!this.interrupted) {
            // Not stopped or interrupted, which means that the utterance came to a natural stop.
            if (this.currentSentence + 1 < this.sentences.length) {
                // If there are more sentences remaining, play the next one
                this.play(this.activeElement, this.currentSentence + 1);
            } else {
                // If we're out of sentences, see if there are any more elements to play.
                const nextReadableElement = this._seekToNextReadableElement(this.activeElement);
                if (nextReadableElement !== null) {
                    this.play(nextReadableElement);
                } else {
                    // No more readable nodes
                    this.reset();
                    this.onOutOfElements();
                }
            }
        }
    }

    /**
     * @param {TtsSentence} s Sentence data
     * @param {Number} i Index of sentence within sentence array
     * @param {TtsPlayer} player TtsPlayer object
     * @returns {Function} Handler function for SpeechSynthesisUtterance events
     */
    makeTtsEventHandler(s, i, player, timeout) {
        return function(event) {
            switch(event.type) {
                case 'start':
                    player.highlightSentence(s.range);
                    player.updateViewerPosition(s.range);
                    player.currentSentence = i;
                    player.cursorPosition = s.range;
                    player.interrupted = false;
                    player.stopped = false;
                    break;
                case 'boundary':
                    // Don't do any of this if speech is stopped or interrupted
                    if (!player.stopped && !player.interrupted) {
                        if (event.name === 'word') {
                            const wordIndexes = s.wordsIn(event.charIndex, event.charLength);
                            player.clearWordHighlight();
                            if (wordIndexes.length > 0) {
                                const range = Fragment.spannedRange(
                                    s.words[wordIndexes[0]], s.words[wordIndexes[wordIndexes.length - 1]]);
                                player.highlightWord(range);
                                player.updateViewerPosition(range);
                            }
                        }    
                    }
                    break;
                case 'error':
                case 'end':
                    player.clearWordHighlight();
                    player.clearSentenceHighlight();
                    player.handleSentenceEnd();
                    clearInterval(timeout);
                    break;
                default:
                    // shrug
            }
        }
    }

    /**
     * Locates the closest Element ancestor of the provided Node that contains readable content.
     * @param {Node} fromNode
     * @returns {Element} Closest readable element ancestor
     */
    _findClosestReadableAncestor(fromNode) {
        if (
            fromNode.nodeType === Node.ELEMENT_NODE &&
            (this._isAtRoot(fromNode) || this._shouldRead(fromNode))
        ) {
            return fromNode;
        }
        const parentElement = fromNode?.parentElement;
        return (parentElement !== null) ? this._findClosestReadableAncestor(parentElement) : null;
    }


    /**
     * Bidirectional search for readable elements in a document tree.
     * @param {Element} fromElement
     * @param {Number} direction
     * @returns {Element} Next Element containing readable content, or null if no following readable element exists.
     */
    _seekToNextReadableElement(fromElement, direction = TtsPlayer.SEEK_FORWARD) {
        this.treeWalker.currentNode = fromElement;
        let node = null;
        if (direction === TtsPlayer.SEEK_BACKWARD) {
            node = this.treeWalker.previousSibling();
        } else {
            node = this.treeWalker.nextSibling();
        }

        if (node !== null) {
            const r = node.ownerDocument.createRange();
            if (node.nodeType === Node.ELEMENT_NODE) {
                // For element nodes, the virtual cursor is positioned as a range surrounding the element.
                r.setStartBefore(node);
                r.setEndAfter(node);
            } else {
                // For text nodes, set the virtual cursor by using a collapsed range.
                // For forward seeks, use the first occurence of a word character.
                // For backwards seeks, use the *last* occurence of a word character.
                if (direction === TtsPlayer.SEEK_BACKWARD) {
                    r.setStart(node, node.textContent.match(/\w(\W*)$/)?.index);
                } else {
                    r.setStart(node, node.textContent.match(WORD_CHAR_MATCHER)?.index);
                }
                r.collapse();
            }

            this.cursorPosition = r;

            return this._findClosestReadableAncestor(node);
        } else {
            return null;
        }
    }

    /**
     * TODO: We might be able to ditch this and fold it into the node filter.
     * @param {Element} element
     * @returns {boolean} true if the element is as low as we can practically go in the DOM tree.
     *                    Generally this means the element contains non-empty text nodes, non-block
     *                    children, or is itself announceable.
     */
    _shouldRead(element) {
        if (
            this._isVisible(element) &&
            (
                this._isAnnounceableElement(element) ||
                (
                    isBlockElement(element) &&
                    (
                        hasReadableTextNodeChildren(element) ||
                        hasNonBlockChildren(element)
                    )
                )
            )
        ) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * @param {Node} node
     * @returns {boolean} True if the node is visible.
     */
    _isVisible(node) {

        let element = (node.nodeType === Node.ELEMENT_NODE) ? node : node.parentElement;
        return (
            element.hidden === false &&
            element.ownerDocument.defaultView.getComputedStyle(element).display !== 'none'
        );
    }

    /**
     * Some elements don't present textual content, but can be announced if the user
     * opts to have it read out. Main examples of this are images (which may have alt text)
     * and page numbers, which are usually marked up as empty spans or divs.
     * @param {Element} element
     * @returns {boolean} True if the element should be considered readable.
     */
    _isAnnounceableElement(element) {
        return (element.nodeType === Node.ELEMENT_NODE) && (
            (this.voiceImageDescriptions && element.tagName === 'IMG') ||
            (this.voicePageNumbers && element.getAttribute('epub:type') === 'pagebreak') ||
            (this.voicePageNumbers && element.getAttribute('role') === 'doc-pagebreak') ||
            (element.tagName === 'MJX-CONTAINER')
        );
    }

    /**
     * @param {Element} element 
     * @returns {boolean} Returns true if the element matches the TtsPlayer's root element.
     */
    _isAtRoot(element) {
        return element === this.treeWalker?.root
    }

    _nodeFilter(n) {
        if (!this._isVisible(n)) {
            return NodeFilter.FILTER_REJECT;
        } else if (isReadableTextNode(n) || this._isAnnounceableElement(n)) {
            return NodeFilter.FILTER_ACCEPT;
        }

        return NodeFilter.FILTER_SKIP;
    }

    /**
     * Locates sentence index that overlaps with Node n.
     * @param {Node} n
     */
    _findIndexForNode(n) {
        const r = n.ownerDocument.createRange();
        if (n.nodeType === Node.ELEMENT_NODE) {
            r.setStartBefore(n);
        } else {
            r.setStart(n, n.textContent.match(WORD_CHAR_MATCHER)?.index ?? 0);
        }
        r.collapse();
        return this._findIndexForRange(r);
    }

    /**
     * Locates sentence index that overlaps with Range r. If an overlap
     * cannot be found, we fall back to index 0.
     * @param {Range} r
     * @returns {number}
     */
    _findIndexForRange(r) {
        if (r !== null && r.commonAncestorContainer.ownerDocument === this.root.ownerDocument) {
            for (let i = 0; i < this.sentences.length; i++) {
                if (this.sentences[i].containsRange(r)) {
                    return i;
                }
            }
        }
        return 0;
    }

}

// The functions that follow are separate from the class as they are pure utility
// functions and do not use instance state variables to do their work.

/**
 * @param {Element} element 
 * @returns {boolean} True if element has text children that contain word characters.
 */
export function hasReadableTextNodeChildren(element) {
    if (element.nodeType === Node.ELEMENT_NODE) {
        for (const n of element.childNodes) {
            if (isReadableTextNode(n)) {
                return true;
            }
        }
    }
    return false;
}

/**
 * @param {Node} node
 * @returns {boolean} True if text node contains word characters.
 */
export function isReadableTextNode(node) {
    return (node.nodeType === Node.TEXT_NODE) && (node.textContent.match(WORD_CHAR_MATCHER));
}

/**
 * @param {Element} element 
 * @returns {boolean} True if element has non-block element children
 */
export function hasNonBlockChildren(element) {
    if (element.nodeType === Node.ELEMENT_NODE) {
        for (const e of element.children) {
            if (!isBlockElement(e)) {
                return true;
            }
        }
    }
    return false;
}

export default TtsPlayer;